import {
  createBrowserRouter,
  redirect,
  type LoaderFunctionArgs,
  type RouteObject,
} from 'react-router-dom';
import { DEFAULT_LANGUAGE, SUPPORTED_LANGUAGES, type Language } from '@models/i18n/language.model';
import { ErrorBoundary } from '@shared/components/ErrorBoundary/ErrorBoundary';

const isSupportedLanguage = (language: string | undefined): language is Language =>
  typeof language === 'string' && SUPPORTED_LANGUAGES.includes(language as Language);

const appendSearch = (path: string, requestUrl?: string): string => {
  if (!requestUrl) {
    return path;
  }

  const { search } = new URL(requestUrl);
  return search ? `${path}${search}` : path;
};

const redirectToDefaultLanguage = (path: string, requestUrl?: string): Response =>
  redirect(appendSearch(`/${DEFAULT_LANGUAGE}${path}`, requestUrl));

const validateLanguageOrRedirect = (
  language: string | undefined,
  fallbackPath: string,
  requestUrl?: string,
): Response | null => {
  if (isSupportedLanguage(language)) {
    return null;
  }

  return redirectToDefaultLanguage(fallbackPath, requestUrl);
};

const routes: RouteObject[] = [
  {
    path: '/',
    loader: ({ request }: LoaderFunctionArgs): Response =>
      redirectToDefaultLanguage('/select-passenger', request.url),
  },
  {
    path: '/:lang',
    loader: ({ params, request }: LoaderFunctionArgs): Response | null =>
      validateLanguageOrRedirect(params.lang, '', request.url),
    errorElement: (
      <ErrorBoundary>
        <div />
      </ErrorBoundary>
    ),
    children: [
      {
        index: true,
        lazy: async (): Promise<{ Component: React.ComponentType }> => {
          const { HomePage } = await import('@features/home/pages/HomePage');
          return { Component: HomePage };
        },
      },
    ],
  },
  {
    path: '/:lang/select-passenger',
    errorElement: (
      <ErrorBoundary>
        <div />
      </ErrorBoundary>
    ),
    loader: ({ params, request }: LoaderFunctionArgs): Response | null =>
      validateLanguageOrRedirect(params.lang, '/select-passenger', request.url),
    lazy: async (): Promise<{ Component: React.ComponentType }> => {
      const { LandingPage } = await import('@features/landing/pages/LandingPage');
      return { Component: LandingPage };
    },
  },
  {
    path: '/:lang/group-booking',
    errorElement: (
      <ErrorBoundary>
        <div />
      </ErrorBoundary>
    ),
    loader: ({ params, request }: LoaderFunctionArgs): Response | null =>
      validateLanguageOrRedirect(params.lang, '/group-booking', request.url),
    lazy: async (): Promise<{ Component: React.ComponentType }> => {
      const { GroupBookingPage } = await import('@features/group-booking/pages/GroupBookingPage');
      return { Component: GroupBookingPage };
    },
  },
  {
    path: '/:lang/passenger-details',
    errorElement: (
      <ErrorBoundary>
        <div />
      </ErrorBoundary>
    ),
    loader: ({ params, request }: LoaderFunctionArgs): Response | null =>
      validateLanguageOrRedirect(params.lang, '/passenger-details', request.url),
    lazy: async (): Promise<{ Component: React.ComponentType }> => {
      const { PassengerDetailsPage } =
        await import('@features/passenger-details/pages/PassengerDetailsPage');
      return { Component: PassengerDetailsPage };
    },
  },
  {
    path: '/:lang/seats',
    errorElement: (
      <ErrorBoundary>
        <div />
      </ErrorBoundary>
    ),
    loader: ({ params, request }: LoaderFunctionArgs): Response | null =>
      validateLanguageOrRedirect(params.lang, '/seats', request.url),
    lazy: async (): Promise<{ Component: React.ComponentType }> => {
      const { SeatsPage } = await import('@features/seats/pages/SeatsPage');
      return { Component: SeatsPage };
    },
  },
  {
    path: '/:lang/change-seat/seatmap',
    errorElement: (
      <ErrorBoundary>
        <div />
      </ErrorBoundary>
    ),
    loader: ({ params, request }: LoaderFunctionArgs): Response | null =>
      validateLanguageOrRedirect(params.lang, '/change-seat/seatmap', request.url),
    lazy: async (): Promise<{ Component: React.ComponentType }> => {
      const { ChangeSeatsPage } = await import('@features/seats/pages/SeatsPage');
      return { Component: ChangeSeatsPage };
    },
  },
  {
    path: '/:lang/extras',
    errorElement: (
      <ErrorBoundary>
        <div />
      </ErrorBoundary>
    ),
    loader: ({ params, request }: LoaderFunctionArgs): Response | null =>
      validateLanguageOrRedirect(params.lang, '/extras', request.url),
    lazy: async (): Promise<{ Component: React.ComponentType }> => {
      const { ExtrasPage } = await import('@features/extras/pages/ExtrasPage');
      return { Component: ExtrasPage };
    },
  },
  {
    path: '/:lang/review',
    errorElement: (
      <ErrorBoundary>
        <div />
      </ErrorBoundary>
    ),
    loader: ({ params, request }: LoaderFunctionArgs): Response | null =>
      validateLanguageOrRedirect(params.lang, '/review', request.url),
    lazy: async (): Promise<{ Component: React.ComponentType }> => {
      const { ReviewPage } = await import('@features/review/pages/ReviewPage');
      return { Component: ReviewPage };
    },
  },
  {
    path: '/:lang/paymentvalidate',
    errorElement: (
      <ErrorBoundary>
        <div />
      </ErrorBoundary>
    ),
    loader: ({ params, request }: LoaderFunctionArgs): Response | null =>
      validateLanguageOrRedirect(params.lang, '/paymentvalidate', request.url),
    lazy: async (): Promise<{ Component: React.ComponentType }> => {
      const { PaymentCallbackPage } = await import('@features/review/pages/PaymentCallbackPage');
      return { Component: PaymentCallbackPage };
    },
  },
  {
    path: '/:lang/confirmation',
    errorElement: (
      <ErrorBoundary>
        <div />
      </ErrorBoundary>
    ),
    loader: ({ params, request }: LoaderFunctionArgs): Response | null =>
      validateLanguageOrRedirect(params.lang, '/confirmation', request.url),
    lazy: async (): Promise<{ Component: React.ComponentType }> => {
      const { ConfirmationPage } = await import('@features/confirmation/pages');
      return { Component: ConfirmationPage };
    },
  },
  {
    path: '/:lang/upgrade',
    errorElement: (
      <ErrorBoundary>
        <div />
      </ErrorBoundary>
    ),
    loader: ({ params, request }: LoaderFunctionArgs): Response | null =>
      validateLanguageOrRedirect(params.lang, '/upgrade', request.url),
    lazy: async (): Promise<{ Component: React.ComponentType }> => {
      const { UpgradePage } = await import('@features/upgrade/pages');
      return { Component: UpgradePage };
    },
  },
];

export const router = createBrowserRouter(routes);
